import {
	brandPrimaryMutedBg,
	resolveThemeColors,
	shellTokens,
	statCardTokens,
	statusTokens,
	tableTokens,
	themeTokens,
	authAccentTokens,
	type StatCardVariant,
	type ThemeColorToken,
	type ThemeMode,
	type SidebarShellToken,
	type HeaderShellToken,
} from "@config/theme/tokens";
import { adminColors } from "@config/theme/colors";

export const CSS_VAR = {
	color: {
		primary: "--admin-primary",
		primaryForeground: "--admin-primary-foreground",
		primaryHover: "--admin-primary-hover",
		secondary: "--admin-secondary",
		secondaryForeground: "--admin-secondary-foreground",
		background: "--admin-background",
		foreground: "--admin-foreground",
		surface: "--admin-surface",
		surfaceForeground: "--admin-surface-foreground",
		surfaceMuted: "--admin-surface-muted",
		muted: "--admin-muted",
		mutedForeground: "--admin-muted-foreground",
		border: "--admin-border",
		borderStrong: "--admin-border-strong",
		danger: "--admin-danger",
		dangerHover: "--admin-danger-hover",
		dangerForeground: "--admin-danger-foreground",
		success: "--admin-success",
		successForeground: "--admin-success-foreground",
		warning: "--admin-warning",
		warningForeground: "--admin-warning-foreground",
		info: "--admin-info",
		infoForeground: "--admin-info-foreground",
		focusRing: "--admin-focus-ring",
		overlay: "--admin-overlay",
		primarySoft: "--admin-primary-soft",
		primarySoftHover: "--admin-primary-soft-hover",
		primarySoftBorder: "--admin-primary-soft-border",
		primarySoftText: "--admin-primary-soft-text",
		primaryMuted: "--admin-primary-muted",
		disabled: "--admin-disabled",
		disabledBackground: "--admin-disabled-background",
	},
	status: {
		warningSurface: "--admin-status-warning-surface",
		warningText: "--admin-status-warning-text",
		warningAccent: "--admin-status-warning-accent",
		successSurface: "--admin-status-success-surface",
		successText: "--admin-status-success-text",
		dangerSurface: "--admin-status-danger-surface",
		dangerText: "--admin-status-danger-text",
	},
	table: {
		headerBg: "--admin-table-header-bg",
		headerText: "--admin-table-header-text",
		rowBorder: "--admin-table-row-border",
		bodyText: "--admin-table-body-text",
	},
	shell: {
		sidebarHeaderBg: "--admin-sidebar-header-bg",
		sidebarPrimary: "--admin-sidebar-primary",
		sidebarForeground: "--admin-sidebar-fg",
		sidebarNavActiveBg: "--admin-sidebar-nav-active-bg",
		sidebarNavActiveFg: "--admin-sidebar-nav-active-fg",
		sidebarNavActiveIcon: "--admin-sidebar-nav-active-icon",
		sidebarNavActiveIndicator: "--admin-sidebar-nav-active-indicator",
		sidebarNavHoverBg: "--admin-sidebar-nav-hover-bg",
		sidebarBorder: "--admin-sidebar-border",
		sidebarScrollbarThumb: "--admin-sidebar-scrollbar-thumb",
		sidebarScrollbarTrack: "--admin-sidebar-scrollbar-track",
		sidebarToggleBg: "--admin-sidebar-toggle-bg",
		sidebarToggleHover: "--admin-sidebar-toggle-hover",
		sidebarToggleFg: "--admin-sidebar-toggle-fg",
		headerBg: "--admin-header-bg",
		headerFg: "--admin-header-fg",
		headerIcon: "--admin-header-icon",
		headerBorder: "--admin-header-border",
	},
	auth: {
		knowledge: "--admin-auth-knowledge",
		skills: "--admin-auth-skills",
		behavior: "--admin-auth-behavior",
		heading: "--admin-auth-heading",
	},
	font: {
		sans: "--admin-font-sans",
		mono: "--admin-font-mono",
	},
	radius: {
		sm: "--admin-radius-sm",
		md: "--admin-radius-md",
		lg: "--admin-radius-lg",
		xl: "--admin-radius-xl",
	},
	shadow: {
		sm: "--admin-shadow-sm",
		card: "--admin-shadow-card",
		md: "--admin-shadow-md",
		lg: "--admin-shadow-lg",
	},
	layout: {
		sidebar: "--admin-sidebar-width",
		sidebarCollapsed: "--admin-sidebar-collapsed-width",
		header: "--admin-header-height",
		footer: "--admin-footer-height",
	},
	component: {
		btnHeightSm: "--admin-btn-height-sm",
		btnHeightMd: "--admin-btn-height-md",
		btnHeightLg: "--admin-btn-height-lg",
		btnRadius: "--admin-btn-radius",
		inputHeight: "--admin-input-height",
		inputRadius: "--admin-input-radius",
		tabInactiveBg: "--admin-tab-inactive-bg",
		tabInactiveHoverBg: "--admin-tab-inactive-hover-bg",
	},
	action: {
		edit: "--admin-action-edit",
		editSurface: "--admin-action-edit-surface",
	},
	statCard: (variant: StatCardVariant) => ({
		accent: `--admin-stat-${variant}-accent`,
		surface: `--admin-stat-${variant}-surface`,
	}),
} as const;

const sidebarTokenToCssVar: Record<SidebarShellToken, string> = {
	headerBackground: CSS_VAR.shell.sidebarHeaderBg,
	headerBorder: CSS_VAR.shell.sidebarBorder,
	headerBorderWidth: CSS_VAR.shell.sidebarBorder,
	primary: CSS_VAR.shell.sidebarPrimary,
	foreground: CSS_VAR.shell.sidebarForeground,
	navActiveBg: CSS_VAR.shell.sidebarNavActiveBg,
	navActiveForeground: CSS_VAR.shell.sidebarNavActiveFg,
	navActiveIcon: CSS_VAR.shell.sidebarNavActiveIcon,
	navActiveIndicator: CSS_VAR.shell.sidebarNavActiveIndicator,
	navHoverBg: CSS_VAR.shell.sidebarNavHoverBg,
	border: CSS_VAR.shell.sidebarBorder,
	scrollbarThumb: CSS_VAR.shell.sidebarScrollbarThumb,
	scrollbarTrack: CSS_VAR.shell.sidebarScrollbarTrack,
	toggleBackground: CSS_VAR.shell.sidebarToggleBg,
	toggleHover: CSS_VAR.shell.sidebarToggleHover,
	toggleForeground: CSS_VAR.shell.sidebarToggleFg,
};

const headerTokenToCssVar: Record<HeaderShellToken, string> = {
	background: CSS_VAR.shell.headerBg,
	foreground: CSS_VAR.shell.headerFg,
	icon: CSS_VAR.shell.headerIcon,
	border: CSS_VAR.shell.headerBorder,
};

const colorTokenToCssVar: Record<ThemeColorToken, string> = {
	primary: CSS_VAR.color.primary,
	primaryForeground: CSS_VAR.color.primaryForeground,
	primaryHover: CSS_VAR.color.primaryHover,
	secondary: CSS_VAR.color.secondary,
	secondaryForeground: CSS_VAR.color.secondaryForeground,
	background: CSS_VAR.color.background,
	foreground: CSS_VAR.color.foreground,
	surface: CSS_VAR.color.surface,
	surfaceForeground: CSS_VAR.color.surfaceForeground,
	surfaceMuted: CSS_VAR.color.surfaceMuted,
	muted: CSS_VAR.color.muted,
	mutedForeground: CSS_VAR.color.mutedForeground,
	border: CSS_VAR.color.border,
	borderStrong: CSS_VAR.color.borderStrong,
	danger: CSS_VAR.color.danger,
	dangerHover: CSS_VAR.color.dangerHover,
	dangerForeground: CSS_VAR.color.dangerForeground,
	success: CSS_VAR.color.success,
	successForeground: CSS_VAR.color.successForeground,
	warning: CSS_VAR.color.warning,
	warningForeground: CSS_VAR.color.warningForeground,
	info: CSS_VAR.color.info,
	infoForeground: CSS_VAR.color.infoForeground,
	focusRing: CSS_VAR.color.focusRing,
	overlay: CSS_VAR.color.overlay,
	primarySoft: CSS_VAR.color.primarySoft,
	primarySoftHover: CSS_VAR.color.primarySoftHover,
	primarySoftBorder: CSS_VAR.color.primarySoftBorder,
	primarySoftText: CSS_VAR.color.primarySoftText,
	disabled: CSS_VAR.color.disabled,
	disabledBackground: CSS_VAR.color.disabledBackground,
};

function getStatCardCssVariables(): Record<string, string> {
	const entries: [string, string][] = [];
	for (const variant of Object.keys(statCardTokens) as StatCardVariant[]) {
		const colors = statCardTokens[variant];
		const vars = CSS_VAR.statCard(variant);
		entries.push([vars.accent, colors.accent], [vars.surface, colors.surface]);
	}
	return Object.fromEntries(entries);
}

function getStatusCssVariables(): Record<string, string> {
	return {
		[CSS_VAR.status.warningSurface]: statusTokens.warningSurface,
		[CSS_VAR.status.warningText]: statusTokens.warningText,
		[CSS_VAR.status.warningAccent]: statusTokens.warningAccent,
		[CSS_VAR.status.successSurface]: statusTokens.successSurface,
		[CSS_VAR.status.successText]: statusTokens.successText,
		[CSS_VAR.status.dangerSurface]: statusTokens.dangerSurface,
		[CSS_VAR.status.dangerText]: statusTokens.dangerText,
	};
}

function getTableCssVariables(): Record<string, string> {
	return {
		[CSS_VAR.table.headerBg]: tableTokens.headerBg,
		[CSS_VAR.table.headerText]: tableTokens.headerText,
		[CSS_VAR.table.rowBorder]: tableTokens.rowBorder,
		[CSS_VAR.table.bodyText]: tableTokens.bodyText,
	};
}

function getShellCssVariables(): Record<string, string> {
	const sidebarVars = Object.fromEntries(
		(Object.keys(sidebarTokenToCssVar) as SidebarShellToken[]).map(key => [
			sidebarTokenToCssVar[key],
			shellTokens.sidebar[key],
		]),
	);
	const headerVars = Object.fromEntries(
		(Object.keys(headerTokenToCssVar) as HeaderShellToken[]).map(key => [
			headerTokenToCssVar[key],
			shellTokens.header[key],
		]),
	);
	return { ...sidebarVars, ...headerVars };
}

export function getCssVariables(mode: ThemeMode = "light"): Record<string, string> {
	const colors = resolveThemeColors(mode);
	const { shared } = themeTokens;

	const colorVars = Object.fromEntries(
		(Object.keys(colorTokenToCssVar) as ThemeColorToken[]).map(key => [
			colorTokenToCssVar[key],
			colors[key],
		]),
	);

	return {
		...colorVars,
		[CSS_VAR.color.primaryMuted]: brandPrimaryMutedBg,
		...getShellCssVariables(),
		[CSS_VAR.auth.knowledge]: authAccentTokens.knowledge,
		[CSS_VAR.auth.skills]: authAccentTokens.skills,
		[CSS_VAR.auth.behavior]: authAccentTokens.behavior,
		[CSS_VAR.auth.heading]: authAccentTokens.heading,
		[CSS_VAR.font.sans]: shared.typography.fontFamily.sans,
		[CSS_VAR.font.mono]: shared.typography.fontFamily.mono,
		[CSS_VAR.radius.sm]: shared.radius.sm,
		[CSS_VAR.radius.md]: shared.radius.md,
		[CSS_VAR.radius.lg]: shared.radius.lg,
		[CSS_VAR.radius.xl]: shared.radius.xl,
		[CSS_VAR.shadow.sm]: shared.shadow.sm,
		[CSS_VAR.shadow.card]: shared.shadow.card,
		[CSS_VAR.shadow.md]: shared.shadow.md,
		[CSS_VAR.shadow.lg]: shared.shadow.lg,
		[CSS_VAR.layout.sidebar]: shared.layout.sidebarWidth,
		[CSS_VAR.layout.sidebarCollapsed]: shared.layout.sidebarCollapsedWidth,
		[CSS_VAR.layout.header]: shared.layout.headerHeight,
		[CSS_VAR.layout.footer]: shared.layout.footerHeight,
		[CSS_VAR.component.btnHeightSm]: themeTokens.components.button.heightSm,
		[CSS_VAR.component.btnHeightMd]: themeTokens.components.button.heightMd,
		[CSS_VAR.component.btnHeightLg]: themeTokens.components.button.heightLg,
		[CSS_VAR.component.btnRadius]: themeTokens.components.button.radius,
		[CSS_VAR.component.inputHeight]: themeTokens.components.input.height,
		[CSS_VAR.component.inputRadius]: themeTokens.components.input.radius,
		[CSS_VAR.component.tabInactiveBg]: adminColors.tab.inactiveBg,
		[CSS_VAR.component.tabInactiveHoverBg]: adminColors.tab.inactiveHoverBg,
		[CSS_VAR.action.edit]: adminColors.semantic.actionEdit,
		[CSS_VAR.action.editSurface]: adminColors.semantic.actionEditSurface,
		...getStatCardCssVariables(),
		...getStatusCssVariables(),
		...getTableCssVariables(),
	};
}
